import { Component, type ErrorInfo, type ReactNode } from "react";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import ButtonIcon from "../Button/ButtonIcon";

/** Localized fallback copy — threaded in from the functional wrapper
 *  because a class component can't call the `useTranslation` hook. */
interface FallbackCopy {
  title: string;
  hint: string;
  retry: string;
}

/**
 * Feature-level boundary used at every routed page. Two reasons:
 *
 *   1. **Crash containment.** A render-time exception in one route
 *      shouldn't blank the entire app. Catching here lets the rest
 *      of the shell (top nav, ticket dropdown) stay interactive
 *      while the broken view is replaced with a recoverable
 *      fallback.
 *
 *   2. **React Query reset wiring.** The inner
 *      `QueryErrorResetBoundary` exposes a `reset()` callback that
 *      clears every query in error state when the user clicks
 *      "Try again". Without it, retry would just re-render the
 *      same cached failure.
 *
 * Phase 4 ships this without third-party error reporting (no
 * Sentry). The deferred observability phase plugs into the
 * `componentDidCatch` hook below — for now we log to the console
 * so dev errors are still visible.
 */

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class InnerErrorBoundary extends Component<
  ErrorBoundaryProps & { onReset: () => void; copy: FallbackCopy },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surfaced to the console for now — observability phase will
    // forward this to whatever reporter we adopt.
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    this.props.onReset();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    const { copy } = this.props;
    return (
      <div className="error-boundary-fallback">
        <h2>{copy.title}</h2>
        <p>{copy.hint}</p>
        <ButtonIcon
          id="error-boundary-retry"
          className="error-boundary-retry"
          label={copy.retry}
          leftIcon="Sync"
          onClick={this.handleReset}
        />
      </div>
    );
  }
}

export default function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  const { t } = useTranslation("common");
  const copy: FallbackCopy = {
    title: t("errorPages.pageError"),
    hint: t("errorPages.pageErrorHint"),
    retry: t("errorPages.tryAgain"),
  };
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <InnerErrorBoundary onReset={reset} fallback={fallback} copy={copy}>
          {children}
        </InnerErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}
